































































import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
import AttributeButtons from './AttributeButtons.vue';
import DraggableIcon from './DraggableIcon.vue';
import dayjs from 'dayjs';
import { DateOrString } from '@/jbi-shared/types/full-document-revision-object.types';
import { GroupLevelAttributeValue } from '@/jbi-shared/types/jaas-group-level-attributes.types';

export interface Timepicker {
  incrementMinutes: number;
}

@Component({
  components: {
    AttributeButtons,
    DraggableIcon
  }
})
export default class GroupLevelAttributeDateTimeField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop({ default: false }) overrideDisableDraggable!: boolean;
  @Prop({ default: false }) overrideDisableAttributeButton!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() minDate!: DateOrString;
  @Prop() maxDate!: DateOrString;

  showField: boolean = false;
  labelErrorMsg: string = '';
  fieldErrorMsg: string = '';
  dateValue: Date | null = null;
  timepicker: Timepicker = {
    incrementMinutes: 15 // 15 minute increments for selection
  };

  /**
   * Date time fields can share the same date, but the end time must be at least
   * 15 minutes after the start time (and vice-versa).
   */
  // The minimum end date should be 15 minutes AFTER the selected start date.
  get trueMinDate() {
    if (!this.minDate) {
      return null;
    }

    return dayjs(this.minDate).add(15, 'minute').toDate();
  }

  // The maximum start date should be 15 minutes BEFORE the end date.
  get trueMaxDate() {
    if (!this.maxDate) {
      return null;
    }

    return dayjs(this.maxDate).subtract(15, 'minute').toDate();
  }

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue.groupLevelAttributeSpec.isRequired;
  }

  mounted() {
    if (this.attributeValue.value) {
      this.dateValue = dayjs(this.attributeValue.value as string).toDate();
    }
  }

  clearDate() {
    this.dateValue = null;
    this.attributeValue.value = null;
  }

  formatDate(date?: Date) {
    return dayjs(this.dateValue?.toISOString()).format('DD MMM YYYY h:mmA');
  }

  @Watch('dateValue')
  assignDateValue() {
    const valueToReturn = this.dateValue
      ? dayjs(this.dateValue).toISOString()
      : null;

    // Update the attribute value
    this.attributeValue.value = valueToReturn;

    if (this.attributeValue.label === 'Start Date') {
      this.returnStartDate();
    }

    if (this.attributeValue.label === 'End Date') {
      this.returnEndDate();
    }
  }

  @Watch('isRequired', { immediate: true })
  updateErrorState() {
    if (this.attributeValue.value) {
      this.validateRequiredFieldValues();
      return;
    }

    this.attributeValue.hasFieldError = true;
    this.updateAttributeValue(this.attributeValue);
  }

  @Watch('attributeValue.value')
  validateRequiredFieldValues() {
    if (this.isRequired && !this.attributeValue.value) {
      this.fieldErrorMsg = `${this.attributeValue.label} is required.`;
      this.attributeValue.hasFieldError = true;
      this.updateAttributeValue(this.attributeValue);
      return;
    }

    this.fieldErrorMsg = '';
    this.attributeValue.hasFieldError = false;
    this.updateAttributeValue(this.attributeValue);
  }

  @Emit('start-date')
  returnStartDate() {
    return this.dateValue;
  }

  @Emit('end-date')
  returnEndDate() {
    return this.dateValue;
  }
}
